import {
  tvaultServiceGet,
  tvaultServicePatch,
  tvaultServicePost,
} from '../lib/utils.js'
import { ONETIME } from '../lib/ComcastBilling.js'
import configuration from '../configuration.js'
import { store } from '../store.js'
import { formatAmount } from '../lib/helpers.ts'

const XFINITY = 'xfinity'
const PASSPORT_ENABLED = 'true'
const UPDATE_DONATION_CAMPAIGN_ACTION = 'UPDATE_DONATION_CAMPAIGN'

const ACTIVE_STATUS = 'active'
const PAST_DUE_STATUS = 'past_due'

export async function getStationDonationAmounts(stationCallSign) {
  const url = `${configuration.apiUrls.getInAppDonationsEligibility
    .replace('[STATION_ID]', stationCallSign)
    .replace('[ACTIVE_PERIOD]', new Date().toISOString())
    .replace('[PLATFORMS]', XFINITY)
    .replace('[PASSPORT_ENABLED]', PASSPORT_ENABLED)}`

  return tvaultServiceGet(url)
    .then((response) => {
      if (response.data.length === 0) {
        return Promise.resolve({
          station_has_donation_campaign: false,
        })
      }
      const activeCampaign = response.data[0]
      const campaignId = activeCampaign.id
      const attributes = activeCampaign.attributes
      return Promise.resolve({
        station_has_donation_campaign: true,
        id: campaignId,
        minimum_monthly_amount: formatAmount(attributes.minimum_monthly_amount),
        monthly_amount_options: attributes.monthly_amount_options.map(
          (singleAmount, index) => ({
            id: `monthly-amount-${index}`,
            amount: formatAmount(singleAmount),
          })
        ),
        minimum_amount: formatAmount(attributes.minimum_amount),
        onetime_amount_options: attributes.onetime_amount_options.map(
          (singleAmount, index) => ({
            id: `one-time-amount-${index}`,
            amount: formatAmount(singleAmount),
          })
        ),
      })
    })
    .catch((err) => {
      return Promise.reject(err)
    })
}

export function getStationDonationAmountsHelper(callSign) {
  getStationDonationAmounts(callSign)
    .then((data) => {
      store.dispatch({
        type: UPDATE_DONATION_CAMPAIGN_ACTION,
        payload: data,
      })
    })
    .catch((err) => {
      if (err.status === 403) {
        store.dispatch({
          type: UPDATE_DONATION_CAMPAIGN_ACTION,
          payload: {
            station_has_donation_campaign: false,
          },
        })
      }
      console.error(err)
    })
}

export async function sendDonationToTVault(
  donationFrequency,
  stationCallSign,
  data
) {
  const { setTransactionDonation, setSubscriptionDonation } =
    configuration.apiUrls
  const url = (
    donationFrequency === ONETIME
      ? setTransactionDonation
      : setSubscriptionDonation
  ).replace('[STATION_ID]', stationCallSign)

  return tvaultServicePost(url, data)
}

export async function getTVaultDonationStatus(
  donationFrequency,
  stationId,
  donationId
) {
  const { getTransactionDonationStatus, getSubscriptionDonationStatus } =
    configuration.apiUrls
  const url = (
    donationFrequency === ONETIME
      ? getTransactionDonationStatus
      : getSubscriptionDonationStatus
  )
    .replace('[STATION_ID]', stationId)
    .replace('[DONATION_ID]', donationId)
  return tvaultServiceGet(url)
}

export async function sendDonationCancellation(stationId, donationId) {
  const url = configuration.apiUrls.cancelDonation
    .replace('[STATION_ID]', stationId)
    .replace('[DONATION_ID]', donationId)

  return tvaultServicePatch(url, {
    data: {
      id: donationId,
      type: 'Subscription',
      attributes: {
        status: 'canceled',
      },
    },
  })
}

export async function cancelTVaultDonation(station, subscriptionId) {
  return sendDonationCancellation(station, subscriptionId).then(
    (response) => response.data.attributes.status
  )
}

export async function getDonationInformation(userPid) {
  const url = configuration.apiUrls.getDonationInformation.replace(
    '[PID]',
    userPid
  )

  return tvaultServiceGet(url).catch((error) => Promise.reject({ error, url }))
}

export async function getTVaultDonationInformation(userPid) {
  return getDonationInformation(userPid)
}

export async function checkDonationStatusPerUser(
  userPid,
  userDonationStatusArr = []
) {
  try {
    const donationResponse = await getTVaultDonationInformation(userPid)
    const { status: donationStatus } = donationResponse.data.attributes

    return {
      checkRulesFulfilled: checkDonationStatus(
        donationStatus,
        userDonationStatusArr
      ),
    }
  } catch (error) {
    return { checkRulesFulfilled: false, error }
  }
}

function checkDonationStatus(donationStatus, userDonationStatusArr = []) {
  return userDonationStatusArr.includes(donationStatus)
}

async function checkUserDonationStatus(donationStatusArr = []) {
  const response = await checkDonationStatusPerUser(
    store.getState().user.pid,
    donationStatusArr
  )
  const expectedDonationStatus = response.checkRulesFulfilled
  if (
    !expectedDonationStatus &&
    response.error &&
    Object.keys(response.error).length > 0
  ) {
    throw new Error(
      response.error?.message || 'Error getting donation information'
    )
  }
  return expectedDonationStatus
}

export async function isDonationActiveOrPastDue() {
  return await checkUserDonationStatus([ACTIVE_STATUS, PAST_DUE_STATUS])
}

export async function isDonationActive() {
  return await checkUserDonationStatus([ACTIVE_STATUS])
}
